import { AccountInfo, EventType, PublicClientApplication } from "@azure/msal-browser";
import { checkLoggedIn, msalConfig, useLogin } from "./authConfig";
import { useEffect, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { Loader } from "@mantine/core";
import { AuthProvider } from "./loginContext";
import { HistoryManagerProvider } from "./components/HistoryProviders/HistoryManagerContext";
import { UserSettingsProvider } from "./UserSettingsContext";
import { SettingsProvider } from "./SettingsContext";
import { Outlet } from "react-router-dom";
import { useConfigs } from "./hooks/useConfigs";
import TagManager from "react-gtm-module";
import { CountryProvider } from "./CountryContext";
import { DomainProvider } from "./DomainContext";
import { AuthorizationProvider } from "./AuthorizationContext";

const LayoutWrapper = () => {
    const [loggedIn, setLoggedIn] = useState<boolean | undefined>(undefined); // undefined = still loading
    const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const config = useConfigs();

    useEffect(() => {
        if (!config.googleTagManagerId) {
            return;
        }

        const tagManagerArgs = {
            gtmId: config.googleTagManagerId
        };

        TagManager.initialize(tagManagerArgs);
    }, [config]);

    useEffect(() => {
        if (useLogin) {
            const instance = new PublicClientApplication(msalConfig);
            let callbackId: string | null = null;
            let isMounted = true;

            const initializeInstance = async () => {
                try {
                    await instance.initialize();

                    if (!isMounted) {
                        return;
                    }

                    const accounts = instance.getAllAccounts();
                    if (!instance.getActiveAccount() && accounts.length > 0) {
                        instance.setActiveAccount(accounts[0]);
                    }

                    // Listen for sign-in event and set active account
                    callbackId = instance.addEventCallback(event => {
                        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                            const account = event.payload as AccountInfo;
                            instance.setActiveAccount(account);
                            setLoggedIn(true);
                        }
                    });

                    setMsalInstance(instance);
                } catch (error) {
                    console.error("Failed to initialize MSAL instance:", error);
                    if (isMounted) {
                        setLoggedIn(false);
                        setIsLoading(false);
                    }
                }
            };

            void initializeInstance();

            return () => {
                isMounted = false;
                if (callbackId) {
                    instance.removeEventCallback(callbackId);
                }
            };
        } else {
            // No authentication required, user is effectively "logged in"
            setLoggedIn(true);
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (msalInstance) {
            const fetchLoggedIn = async () => {
                try {
                    const isLoggedIn = await checkLoggedIn(msalInstance);
                    setLoggedIn(isLoggedIn);
                } catch (error) {
                    console.error("Error checking login status:", error);
                    setLoggedIn(false);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchLoggedIn();
        }
    }, [msalInstance]);

    // Show loading while determining auth state
    if (isLoading || loggedIn === undefined) {
        return <Loader size={16} type="dots" />;
    }

    if (useLogin && msalInstance) {
        return (
            <MsalProvider instance={msalInstance}>
                <AuthProvider loggedIn={loggedIn}>
                    <HistoryManagerProvider>
                        <SettingsProvider>
                            <AuthorizationProvider>
                                <UserSettingsProvider>
                                    <CountryProvider>
                                        <DomainProvider>
                                            <Outlet />
                                        </DomainProvider>
                                    </CountryProvider>
                                </UserSettingsProvider>
                            </AuthorizationProvider>
                        </SettingsProvider>
                    </HistoryManagerProvider>
                </AuthProvider>
            </MsalProvider>
        );
    }

    return (
        <AuthProvider loggedIn={loggedIn}>
            <HistoryManagerProvider>
                <SettingsProvider>
                    <AuthorizationProvider>
                        <UserSettingsProvider>
                            <Outlet />
                        </UserSettingsProvider>
                    </AuthorizationProvider>
                </SettingsProvider>
            </HistoryManagerProvider>
        </AuthProvider>
    );
};

export default LayoutWrapper;
